/** Shared helpers for AccrualBar (joining) and ProRatedBar (leaving).
 *
 *  Each helper mirrors a counterpart in
 *  ``services/api/src/domain/accrual.py`` so the UI agrees with the
 *  server-side caps. Constants + tiny utility functions only — no
 *  component code here.
 */

export const MONTH_LABELS: ReadonlyArray<string> = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/** Policy v1.0 §1.2 — Join Year Accrual: 'Joining after the 15th counts
 *  as half a month for that month only.' Mirrors ``_join_month_credit``
 *  in services/api/src/domain/accrual.py. */
export const FULL_MONTH_DAY_THRESHOLD = 15

/** Round DOWN to the nearest 0.5 — matches the docx's only explicit
 *  rounding rule (§1.3 team-capacity "rounded down") and the server's
 *  ``_floor_half``. Used for the yearly slice. */
export function floorHalf(n: number): number {
  return Math.floor(n * 2) / 2
}

/** HR's monthly-drip rounding rule. Output is always N.0 or N.5:
 *    value <  N+0.5      → N.0
 *    value ≈  N+0.5      → N+0.5 (within ±1e-4 tolerance for IEEE-754
 *                                  wobble on things like 14/12 × 3)
 *    value >  N+0.5      → (N+1).0
 *  Mirrors ``_hr_round`` in services/api/src/domain/accrual.py. */
export function hrRound(n: number): number {
  const floorN = Math.floor(n)
  const frac = n - floorN
  const EPS = 1e-4
  if (frac < 0.5 - EPS) return floorN
  if (frac <= 0.5 + EPS) return floorN + 0.5
  return floorN + 1
}

/** HR's leaver rounding rule (2026-10 handwritten sheet). Applied to
 *  the fractional part: 0.01–0.49 → down, 0.50–0.59 → keep .5,
 *  0.60+ → up to the next whole day. ±1e-4 tolerance absorbs
 *  IEEE-754 wobble on a "should be exactly N.5" value.
 *  Mirrors ``_round_half`` in services/api/src/domain/accrual.py —
 *  used for leaver per-day proration: 14.99 → 15.0, 14.15 → 14.0,
 *  14.5 stays 14.5, 14.6 → 15.0. */
export function roundHalf(n: number): number {
  const floorN = Math.floor(n)
  const frac = n - floorN
  const EPS = 1e-4
  if (frac < 0.5 - EPS) return floorN
  if (frac < 0.6 - EPS) return floorN + 0.5
  return floorN + 1
}

export function parseUtcDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`)
}

export function joinMonthCredit(join: Date): number {
  return join.getUTCDate() <= FULL_MONTH_DAY_THRESHOLD ? 1 : 0.5
}

export function isLeapYear(y: number): boolean {
  return y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)
}

export function formatExitDate(iso: string): string {
  const d = parseUtcDate(iso)
  return `${d.getUTCDate()} ${MONTH_LABELS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}
